import React, { useMemo, useState, useEffect, useContext } from "react";
import { Link } from "react-router-dom";
import { AuthContext } from "../context/AuthContextProvider";
import { RequireAuth } from "../RequireAuth.jsx";
import { useGetPeoples, useGetOrgProfile } from "../api/settings";

// MUI
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import Avatar from "@mui/material/Avatar";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

// MUI Icons (used instead of Iconify)
import CheckIcon from "@mui/icons-material/Check";
import LocalHospitalIcon from "@mui/icons-material/LocalHospital";
import GroupAddIcon from "@mui/icons-material/GroupAdd";
import CreditCardOutlinedIcon from "@mui/icons-material/CreditCardOutlined";
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
const ProfileSteps = () => {
  const { allCenterIds, loading, user } = useContext(AuthContext);
  // Migrated businesses skip onboarding altogether (see shouldShow below) -
  // userType stays plain "b2b", migration is a separate flag.
  const isMigratedUser = user?.userType === "b2b" && user?.migration === true;

  // const centreIds = useMemo(
  //   () =>
  //     Array.isArray(all_centres)
  //       ? all_centres.map((c) => c?.value || c?.id).filter(Boolean)
  //       : [],
  //   [all_centres]
  // );

  const hasAnyCentre = allCenterIds?.length > 0;

  const {
    peoples,
    isLoading: isMemberLoading,
    isValidating: isMemberValidating,
  } = useGetPeoples(hasAnyCentre ? { centerIds: allCenterIds, limit: 1 } : {});
  const hasAnyMember =
    hasAnyCentre && Array.isArray(peoples) && peoples.length > 0;

  const { orgProfile, isLoading: isOrgLoading, isValidating: isOrgValidating } =
    useGetOrgProfile();
  const hasBilling = !!(
    orgProfile?.company_data &&
    (orgProfile.company_data?.billing_address ||
      orgProfile.company_data?.name ||
      orgProfile.company_data?.tax_id)
  );

  const requireAuthProps = useMemo(() => ({ role: ["b2b"] }), []);

  // The card must be HIDDEN on first paint and only appear once we have positively
  // confirmed — after every data source has settled — that a step is still pending.
  // Driving visibility from an effect (which runs after paint) guarantees the card
  // can never flash in during the initial render while allCenterIds / orgProfile are
  // still empty. It starts hidden and is shown only when genuinely needed.
  const allSettled =
    !loading &&
    !isMemberLoading &&
    !isMemberValidating &&
    !isOrgLoading &&
    !isOrgValidating;
  const onboardingComplete = hasAnyCentre && hasAnyMember && hasBilling;
  const shouldShow = allSettled && !onboardingComplete && !isMigratedUser;

  // Reveal ONLY when the "a step is still pending" state has held steady for a
  // short moment. If any of the centre / member / billing fetches resolves within
  // that window and flips onboarding to complete, the timeout is cleared and the
  // card is never painted — so it can't flash in for a frame on refresh.
  const [reveal, setReveal] = useState(false);
  useEffect(() => {
    if (!shouldShow) {
      setReveal(false);
      return undefined;
    }
    const t = setTimeout(() => setReveal(true), 200);
    return () => clearTimeout(t);
  }, [shouldShow]);

  if (!reveal) return null;


  // Reusable Card component
  const StepCard = ({
    bg = "#fff",
    avatarBg = "#eee",
    avatarIcon = null, // expects a React node (MUI icon)
    avatarColor = "inherit",
    titleSmall,
    titleMain,
    href,
    disabled,
    fontFamily = "figtree",
    borderRadius="20px"
  }) => {
    const Content = (
      <Paper
        elevation={0}
        sx={{
          borderRadius: 2,
          p: 2,
          display: "flex",
          alignItems: "center",
          gap: 2,
          background: bg,
          cursor: disabled ? "not-allowed" : "pointer",
          opacity: disabled ? 0.8 : 1,
          minHeight: 84,
        }}
      >
        <Avatar
          sx={{
            bgcolor: avatarBg,
            width: 44,
            height: 44,
            boxShadow: "none",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {/* avatarIcon is a React node (e.g. <CheckIcon />). We set its color */}
          {avatarIcon &&
            React.cloneElement(avatarIcon, {
              style: { color: avatarColor || "inherit", fontSize: 20 },
            })}
        </Avatar>

        <Box sx={{ display: "flex", flexDirection: "column" }}>
          <Typography variant="caption" sx={{ color: "var(--app-text-muted)" }}>
            {titleSmall}
          </Typography>
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 700, color: "var(--app-text)", mt: 0.25 }}
          >
            {titleMain}
          </Typography>
        </Box>

        <Box sx={{ ml: "auto", color: "var(--app-text)" }}>
          <ChevronRightIcon />
        </Box>
      </Paper>
    );

    if (disabled || !href) {
      return Content;
    }

    return (
      <Link to={href} style={{ textDecoration: "none" }}>
        {Content}
      </Link>
    );
  };

  return (
    <RequireAuth {...requireAuthProps}>
      <Box sx={{ mt: 1, mb: 1 }}>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" },
            gap: 2,
            alignItems: "stretch",
          }}
        >
          {/* Card 1: Centres */}
          <Box>
            {hasAnyCentre ? (
              <StepCard
                bg="#E8FAFF"
                avatarBg="#FFFFFF"
                avatarIcon={<CheckCircleIcon />}
                avatarColor="#348530"
                titleSmall="My Centres"
                titleMain="Add Centre"
                disabled
                fontFamily="figtree"
                borderRadius="20px"
              />
            ) : (
              <StepCard
                bg="#E8FAFF"
                avatarBg="#FFFFFF"
                avatarIcon={<LocalHospitalIcon />}
                avatarColor="var(--app-text)"
                titleSmall="My Centres"
                titleMain="Add Centre"
                href="/add-centre"
                fontFamily="figtree"
                borderRadius="20px"
              />
            )}
          </Box>

          {/* Card 2: People */}
          <Box>
            {hasAnyMember && !isMemberLoading ? (
              <StepCard
                bg="#FFF5E8"
                avatarBg="#FFFFFF"
                avatarIcon={<CheckCircleIcon />}
                avatarColor="#348530"
                titleSmall="My Team"
                titleMain="Add People"
                disabled
                fontFamily="figtree"
                borderRadius="20px"
              />
            ) : (
              <StepCard
                bg="#FFF5E8"
                avatarBg="white"
                avatarIcon={<PeopleAltIcon />}
                avatarColor="var(--app-text)"
                titleSmall="My Team"
                titleMain="Add People"
                href="/add-member"
                fontFamily="figtree"
                borderRadius="20px"
              />
            )}
          </Box>

          {/* Card 3: Billing */}
          <Box>
            {hasBilling ? (
              <StepCard
                bg="#E6FAEB"
                avatarBg="white"
                avatarIcon={<CheckCircleIcon />}
                avatarColor="#348530"
                titleSmall="Organisation Billing"
                titleMain="Add Billing Details"
                disabled
                fontFamily="figtree"
                borderRadius="20px"
              />
            ) : (
              <StepCard
                bg="#F5F7FF"
                avatarBg="#E7E9FF"
                avatarIcon={<CreditCardOutlinedIcon />}
                avatarColor="var(--app-text)"
                titleSmall="Organisation Billing"
                titleMain="Add Billing Details"
                href="/settings"
                fontFamily="figtree"  
                borderRadius="20px"
              />
            )}
          </Box>
        </Box>
      </Box>
    </RequireAuth>
  );
};

export default ProfileSteps;
